import { API_BASE_URL } from "./api-base.ts";
import type { MarketSymbol } from "./market-discovery.ts";
import { PublicReferenceCache, publicReferenceKey, type ReferenceSnapshot } from "./public-reference-cache.ts";

export type MarketReference = {
  schema_version: 1;
  catalog_version: string;
  query: string;
  items: MarketSymbol[];
};
export type MarketReferenceOptions = { assetClass?: string; limit?: number };
const MARKET_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

function text(value: unknown, max: number): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.length <= max
    && !/[\u0000-\u001f\u007f]/.test(value);
}

export function parseMarketReference(value: unknown, query: string, limit = 100): MarketReference {
  if (!value || typeof value !== "object") throw new Error("invalid_market_reference");
  const raw = value as Record<string, unknown>;
  if (raw.schema_version !== 1 || typeof raw.catalog_version !== "string"
    || !/^\d{4}-\d{2}-\d{2}$/.test(raw.catalog_version)
    || raw.query !== query.trim() || !Array.isArray(raw.items) || raw.items.length > limit) {
    throw new Error("invalid_market_reference");
  }
  const seen = new Set<string>();
  const items = raw.items.map((candidate): MarketSymbol => {
    if (!candidate || typeof candidate !== "object") throw new Error("invalid_market_reference");
    const item = candidate as Record<string, unknown>;
    if (!text(item.symbol, 64) || !text(item.displayName, 160) || !text(item.group, 80)
      || !text(item.assetClass, 32) || !/^[a-z][a-z0-9_]*$/.test(item.assetClass)
      || seen.has(item.symbol)) throw new Error("invalid_market_reference");
    seen.add(item.symbol);
    return { symbol: item.symbol, displayName: item.displayName, group: item.group, assetClass: item.assetClass };
  });
  // Whitelist fields: a future response's account/usage/provider payload cannot enter storage.
  return { schema_version: 1, catalog_version: raw.catalog_version, query: query.trim(), items };
}

export class MarketReferenceCache {
  private readonly baseURL: string;
  private readonly cache: PublicReferenceCache;

  constructor(baseURL = API_BASE_URL, cache = new PublicReferenceCache()) {
    this.baseURL = baseURL;
    this.cache = cache;
  }

  read(query: string, locale: string, options: MarketReferenceOptions = {}): ReferenceSnapshot<MarketReference> | undefined {
    try {
      return this.cache.read(this.key(query, locale, options), (raw) => parseMarketReference(raw, query, options.limit ?? 100));
    } catch { return undefined; }
  }

  save(query: string, locale: string, raw: unknown, options: MarketReferenceOptions = {}): MarketReference {
    const value = parseMarketReference(raw, query, options.limit ?? 100);
    this.cache.write(this.key(query, locale, options), value, MARKET_RETENTION_MS);
    return value;
  }

  private key(query: string, locale: string, { assetClass = "all", limit = 100 }: MarketReferenceOptions): string {
    if (query.trim().length > 64 || !Number.isInteger(limit) || limit < 1 || limit > 100) {
      throw new Error("invalid_market_reference_query");
    }
    // Empty-query catalog and searches are separate; never reuse across backends/locales/filters.
    return publicReferenceKey("market-symbols", this.baseURL, [1, locale, query.trim(), assetClass, limit]);
  }
}
